import moment from "moment";
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useDispatch} from "react-redux";
import Modal from "react-responsive-modal";
import "react-responsive-modal/styles.css";
import {
  assignKbaKey,
  getKbaKey,
  updateKbaKeyStatus,
} from "../../actions/kba";
import Button from "../../common/button/button";
import ModalLoadingIndicator from "../../reusable/modalLoadingIndicator";
import {newModalStyle} from "../../reusable/styles";
import {
  canTransitionKbaStatus,
  isKbaKeyValid,
  KBA_DECLINE_REASON_MAX_LENGTH,
  KBA_STATUSES,
  normalizeKbaKey,
} from "../../utils/kba";
import KbaStatusBadge from "./KbaStatusBadge";
import Style from "./kbaIntegrationModal.module.css";

const modalButtonStyle = {height: "40px", width: "140px", fontFamily: "Gilroy-Medium"};
const inlineUpdateButtonStyle = {height: "40px", width: "140px", fontFamily: "Gilroy-Medium", marginRight: 0};

const KbaIntegrationModal = ({
  open,
  onClose,
  zoneId,
  zoneType,
  zoneLabel,
  existingKbaKey,
}) => {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [kbaRecord, setKbaRecord] = useState(null);
  const [keyInput, setKeyInput] = useState("");
  const [error, setError] = useState("");
  // pendingAction: { message, confirmLabel, onConfirm, requiresReason? } — used for the
  // replace-in-place confirmation pattern (see .claude/rules/replace-in-place-modal.md).
  // When requiresReason is set, onConfirm receives the trimmed reason text.
  const [pendingAction, setPendingAction] = useState(null);
  const [reasonInput, setReasonInput] = useState("");

  const existingKeyValue = existingKbaKey?.kbaKey;

  useEffect(() => {
    if (!open || !zoneId) {
      return;
    }

    setLoading(true);
    setError("");
    setKbaRecord(null);
    setKeyInput("");
    setPendingAction(null);
    setReasonInput("");

    const loadKbaState = async () => {
      try {
        const freshKbaKey = await dispatch(getKbaKey(existingKeyValue));
        setKbaRecord(freshKbaKey);
        setKeyInput(freshKbaKey?.kbaKey || existingKeyValue || "");
      } catch (e) {
        setKeyInput(existingKeyValue || "");
        setError(e.response?.data?.error || t("Kba.loadingError"));
      } finally {
        setLoading(false);
      }
    };

    loadKbaState();
  }, [dispatch, existingKeyValue, open, t, zoneId, zoneType]);

  const normalizedInput = normalizeKbaKey(keyInput);
  const isKeyDirty = !!kbaRecord && normalizedInput !== kbaRecord.kbaKey;

  const performKeyUpdate = async () => {
    setSaving(true);
    setError("");
    try {
      const assignedKbaKey = await dispatch(assignKbaKey(zoneType, zoneId, normalizedInput));
      setKbaRecord(assignedKbaKey);
      setKeyInput(assignedKbaKey?.kbaKey || normalizedInput);
    } catch {
      // notification surfaced by the action layer
    } finally {
      setSaving(false);
    }
  };

  const handleSaveKey = () => {
    if (!isKbaKeyValid(normalizedInput)) {
      setError(t("Kba.validationError"));
      return;
    }

    setPendingAction({
      message: t("Kba.confirmKeyChange"),
      confirmLabel: t("Kba.updateKey"),
      onConfirm: performKeyUpdate,
    });
  };

  const performStatusChange = async (status, declineReason) => {
    setSaving(true);
    try {
      const updatedKbaKey = await dispatch(
        updateKbaKeyStatus(kbaRecord.kbaKey, status, declineReason)
      );
      setKbaRecord(updatedKbaKey);
    } catch {
      // notification surfaced by the action layer
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = (status) => {
    if (!kbaRecord?.kbaKey || !canTransitionKbaStatus(kbaRecord.status, status)) {
      return;
    }

    if (status === "INACTIVE") {
      setPendingAction({
        message: t("Kba.confirmInactive"),
        confirmLabel: t("Kba.setInactive"),
        onConfirm: () => performStatusChange(status),
      });
      return;
    }

    if (status === "DECLINED") {
      setPendingAction({
        message: t("Kba.confirmDecline"),
        confirmLabel: t("Kba.decline"),
        requiresReason: true,
        onConfirm: (reason) => performStatusChange(status, reason),
      });
      return;
    }

    performStatusChange(status);
  };

  const handleConfirmPendingAction = async () => {
    const action = pendingAction;
    const reason = reasonInput.trim();
    setPendingAction(null);
    setReasonInput("");
    await action.onConfirm(reason);
  };

  const handleCancelPendingAction = () => {
    setPendingAction(null);
    setReasonInput("");
  };

  const handleDiscardKeyChange = () => {
    setKeyInput(kbaRecord?.kbaKey || "");
    setError("");
  };

  return (
    <Modal
      open={open}
      closeOnEsc
      onClose={onClose}
      center
      styles={newModalStyle({width: "560px"})}
    >
      <div className="modal__headerLine" />
      <div className="modal__title">{t("Kba.title")}</div>
      {loading && <ModalLoadingIndicator />}
      <div className={Style.bodyWrapper}>
        <div className={Style.sectionTitle}>{zoneLabel}</div>

        {pendingAction ? (
          <div className={Style.confirmView}>
            <div className={Style.confirmMessage}>{pendingAction.message}</div>
            {pendingAction.requiresReason && (
              <div className={Style.fieldGroup}>
                <label className={Style.label} htmlFor="kba-reason-input">
                  {t("Kba.declineReason")}
                </label>
                <textarea
                  id="kba-reason-input"
                  className={Style.reasonTextarea}
                  maxLength={KBA_DECLINE_REASON_MAX_LENGTH}
                  value={reasonInput}
                  onChange={(e) => setReasonInput(e.target.value)}
                  disabled={saving}
                  autoFocus
                />
              </div>
            )}
            <div className={Style.actions}>
              <Button
                type="button"
                text={t("Kba.cancel")}
                buttonStyle={{
                  ...modalButtonStyle,
                  backgroundColor: "#ffffff",
                  color: "rgb(0, 90, 146)",
                  border: "1px solid rgb(0, 90, 146)",
                }}
                onClick={handleCancelPendingAction}
                disabled={saving}
              />
              <Button
                type="button"
                text={pendingAction.confirmLabel}
                buttonStyle={modalButtonStyle}
                onClick={handleConfirmPendingAction}
                disabled={
                  saving || (pendingAction.requiresReason && !reasonInput.trim())
                }
              />
            </div>
          </div>
        ) : (
          <>
            <div className={Style.label}>{t("Kba.key")}</div>
            <div className={Style.keyEditRow}>
              <input
                id="kba-key-input"
                className={Style.input}
                maxLength={5}
                value={keyInput}
                onChange={(e) => setKeyInput(normalizeKbaKey(e.target.value))}
                disabled={loading || saving}
              />
              <Button
                type="button"
                text={t("Kba.updateKey")}
                buttonStyle={inlineUpdateButtonStyle}
                onClick={handleSaveKey}
                disabled={
                  loading ||
                  saving ||
                  !isKeyDirty ||
                  !isKbaKeyValid(normalizedInput)
                }
              />
              {isKeyDirty && (
                <button
                  type="button"
                  className={Style.linkButton}
                  onClick={handleDiscardKeyChange}
                  disabled={loading || saving}
                >
                  {t("Kba.discardKeyChange")}
                </button>
              )}
            </div>

            {kbaRecord && (
              <>
                <div className={Style.metaRow}>
                  <span className={Style.summaryLabel}>{t("Kba.statusLabel")}</span>
                  <KbaStatusBadge status={kbaRecord.status} />
                  <span className={Style.metaSeparator}>·</span>
                  <span className={Style.summaryLabel}>{t("Kba.modifiedOn")}</span>
                  <span>
                    {kbaRecord.modifiedOn
                      ? moment(kbaRecord.modifiedOn).format("DD.MM.YYYY HH:mm")
                      : t("Kba.notAvailable")}
                  </span>
                </div>

                <div className={Style.label}>{t("Kba.changeStatus")}</div>
                {isKeyDirty && (
                  <div className={Style.hint}>{t("Kba.unsavedKeyHint")}</div>
                )}
                <div className={Style.statusButtons}>
                  {KBA_STATUSES.map((status) => {
                    const isDisabled =
                      saving ||
                      isKeyDirty ||
                      status === kbaRecord.status ||
                      !canTransitionKbaStatus(kbaRecord.status, status);
                    return (
                      <button
                        key={status}
                        type="button"
                        className={Style.statusButton}
                        disabled={isDisabled}
                        onClick={() => handleStatusChange(status)}
                      >
                        {t(`Kba.status.${status}`)}
                      </button>
                    );
                  })}
                </div>

                {kbaRecord.status === "DECLINED" && kbaRecord.declineReason && (
                  <>
                    <div className={Style.label}>{t("Kba.declineReason")}</div>
                    <div className={Style.declineReason}>{kbaRecord.declineReason}</div>
                  </>
                )}
              </>
            )}
          </>
        )}

        {error && !pendingAction && <div className={Style.error}>{error}</div>}

        {!pendingAction && (
          <div className={Style.actions}>
            <Button
              type="button"
              text={t("Kba.close")}
              buttonStyle={modalButtonStyle}
              onClick={onClose}
            />
          </div>
        )}
      </div>
    </Modal>
  );
};

export default KbaIntegrationModal;
